import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { Badge, DataTable, type BadgeTone, type DataTableColumn, type SelectionConfig } from '../../components/ui';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import type { BillRun } from '../../api/types';

// I5 (dark-mode contrast fix): this verification chip was hand-rolled with
// five literal-colour tones and no `dark:` variants at all, rendered in the
// same flex container as the real `getStatusBadge` <Badge> at :82 — so a
// dark-mode viewer saw one properly-themed chip next to one that never
// adapted. Routed onto Badge's own tone system instead of hand-tuning five
// new dark shades: every tone's own text/bg AA ratio and dark-bg-vs-card
// ratio is already asserted in src/test/contrast.test.ts, so this chip
// inherits that verification instead of needing its own.
function verificationTone(run: Pick<BillRun, 'verification_status' | 'verification_result'>): BadgeTone {
  if (run.verification_status === 'complete') {
    if (run.verification_result === 'major_variance') return 'danger';
    if (run.verification_result === 'minor_variance') return 'warning';
    return 'success';
  }
  if (run.verification_status === 'comparing') return 'info';
  return 'purple';
}

function verificationLabel(run: Pick<BillRun, 'verification_status' | 'verification_result'>): string {
  if (run.verification_status === 'complete') {
    if (run.verification_result === 'major_variance') return 'Discrepancies';
    if (run.verification_result === 'minor_variance') return 'Minor Variances';
    return 'Verified';
  }
  if (run.verification_status === 'comparing') return 'Comparing';
  return 'Verification';
}

interface BillRunsTableProps {
  runs: BillRun[];
  selectedRuns: Map<string, BillRun>;
  eligibleCount: number;
  allEligibleSelected: boolean;
  someEligibleSelected: boolean;
  onToggleRun: (run: BillRun) => void;
  onToggleAll: () => void;
  formatDate: (value: string) => string;
  formatCurrency: (cents: number | null) => string;
  getStatusBadge: (status: string, run?: BillRun) => ReactNode;
}

export default function BillRunsTable({
  runs,
  selectedRuns,
  eligibleCount,
  allEligibleSelected,
  someEligibleSelected,
  onToggleRun,
  onToggleAll,
  formatDate,
  formatCurrency,
  getStatusBadge,
}: BillRunsTableProps) {
  // Same hook, same `lg` breakpoint, same source of truth DataTable itself
  // uses to decide whether to collapse (useMediaQuery.ts) — so density and
  // collapse can never disagree about which side of `lg` we are on. See the
  // `density` prop below for why this table picks per-breakpoint.
  const isDesktop = useIsDesktop();

  const columns: DataTableColumn<BillRun>[] = [
    {
      key: 'period',
      label: 'Period',
      priority: 'secondary',
      render: (run) => (
        <Link to={`/bill-runs/${run.id}`} className="text-accent font-medium">
          {formatDate(run.period_start)} - {formatDate(run.period_end)}
        </Link>
      ),
    },
    {
      key: 'pocket',
      label: 'Pocket / Tenant',
      priority: 'primary',
      // NOT made a link. 4c1309e's handover recommends turning this whole
      // cell into a <Link> to /bill-runs/:id, to buy back the expand tap that
      // moving `View` into the disclosure cost. Declined, four reasons:
      //
      // 1. It cannot cover every row. The cell has two branches and only one
      //    of them has anything to name a link with — a run with no pocket
      //    renders "No pocket". Navigation would then exist on some rows and
      //    not others, which is worse than it costing one tap on all of them.
      // 2. The accessible name would be a tenant, not an action. Wrapping the
      //    cell makes a link announced as "Root Coffee Root Coffee" (pocket
      //    name + tenant chip, both inside it); the status dot and its colour
      //    semantics end up as link content too. An aria-label could paper
      //    over that, at which point the visible text and the accessible name
      //    disagree.
      // 3. Styled to look unchanged (which is what keeps desktop from
      //    regressing) it is an invisible affordance — nothing tells anyone
      //    the cell is tappable. Styled visibly, the pocket name turns accent
      //    and desktop gets three links to the same run per row (Period,
      //    Pocket, View), which is the desktop regression the brief rules out.
      // 4. It buys no width. It is orthogonal to the scroll this file is
      //    fixing, and it is an interaction change, not layout — outside
      //    "layout and column configuration only".
      //
      // The tap cost is real and is left standing deliberately: below `lg`
      // the disclosure holds Period, Bills, Status, Created, Approved AND
      // View, so the expand is a peek-then-open, not a detour. DataTable's
      // docblock records the sanctioned remedy if it is ever judged too
      // expensive — promote the action into a primary column's `render` —
      // and that is a UX call for the user, not a layout fix.
      // The tenant chip repeats the line above it whenever a pocket's active
      // tenant is named the same as the pocket — which is most of this data:
      // "Root Coffee / Root Coffee", "Common Areas / Common Areas",
      // "Block A / Block A". Line :90 above noted this in passing (a wrapping
      // link would announce "Root Coffee Root Coffee") while arguing an
      // unrelated point; it was observed and never treated.
      //
      // Suppressed only when it genuinely duplicates. Pocket name and tenant
      // name are DIFFERENT fields that happen to coincide here, so the chip
      // is kept verbatim the moment they differ, and "Vacant" — which is not
      // a duplicate of anything — is always kept. The dot survives in both
      // branches: it is the occupancy signal and it is what the teal/amber
      // pair encodes. When the text is dropped, `sr-only` "Occupied" replaces
      // it so the fact is not left encoded in colour alone, and so the row's
      // accessible name still distinguishes occupied from vacant.
      //
      // Honest accounting of what this buys, because it is less than it
      // looks: the cell is `flex-col`, so its min-content width is the MAX of
      // its two lines, not their sum. When the two lines hold the same words
      // the chip is not what makes the column wide — the name line is. This
      // reclaims a LINE OF HEIGHT per row and a few px of width (the chip
      // carries a 6px dot + 4px gap the name line does not), not the ~13px
      // the now-unbreakable Rand amount costs. See the report.
      render: (run) => {
        if (!run.pocket_id) return <span className="text-xs text-content-muted italic">No pocket</span>;
        const tenant = run.active_tenant_name;
        const duplicatesPocketName = Boolean(tenant) && tenant === run.pocket_name;
        return (
          <div className="flex flex-col gap-0.5">
            <span className="text-content">{run.pocket_name || '—'}</span>
            <span
              className={`inline-flex items-center gap-1 text-xs ${
                run.active_tenant_id ? 'text-teal-700 dark:text-teal-300' : 'text-amber-700 dark:text-amber-300'
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${run.active_tenant_id ? 'bg-teal-600' : 'bg-amber-500'}`} />
              {duplicatesPocketName ? <span className="sr-only">Occupied</span> : (tenant || 'Vacant')}
            </span>
          </div>
        );
      },
    },
    {
      key: 'bills',
      label: 'Bills',
      priority: 'secondary',
      render: (run) => <span className="text-content-muted">{run.bill_count || 0}</span>,
    },
    {
      key: 'total',
      label: 'Total',
      priority: 'primary',
      numeric: true,
      render: (run) => <span className="font-semibold">{formatCurrency(run.total_amount_cents)}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      // Demoted from 'primary' (2026-07-30 mobile Phase 0): at 375px DataTable
      // also renders a selection checkbox, an expand toggle, and the
      // rowActions "View" cell alongside the primary columns — with all
      // three of pocket/total/status marked primary that's six cells in one
      // row and it overflows. Pocket identifies the row and total is the
      // number people scan for; status is already partly conveyed by the row
      // being selectable at all (only pending_approval runs get a checkbox),
      // and remains one tap away via the row's expand toggle.
      //
      // RE-EVALUATED 2026-07-31, after 4c1309e removed the rowActions cell
      // from the narrow row: STAYS `secondary`. The premise above ("the
      // actions cell competes for width") did expire, but the ruling doesn't,
      // for a stronger reason than the one it was originally made on.
      //
      // Status renders a <Badge>, and Badge is `whitespace-nowrap` by
      // construction (Badge.tsx:41) — deliberately, a chip that wraps
      // mid-label is worse than one that doesn't fit. So this column's
      // min-content width IS its widest label, entire: "pending approval" at
      // text-xs semibold (~106px) plus Badge's own px-2.5 (20px) ≈ 126px,
      // plus the cell's padding — ~150px at `compact`, unshrinkable at ANY
      // viewport. The remaining budget below `lg` after the checkbox column
      // (60px), the toggle column (56px), the unbreakable Rand amount
      // (~85px + padding) and Pocket is ~35px. Promoting Status would
      // reintroduce, at roughly 4x, exactly the overflow this commit exists
      // to remove.
      //
      // This is the branch's general rule stated concretely: a `primary`
      // column must not hold content that can neither wrap nor be bounded.
      // Pocket/Tenant is free text that wraps; Total is unbreakable but
      // bounded (a currency string has a known ceiling); a Badge is neither.
      // If Status must be scannable on a phone, the answer is a bounded
      // representation — an icon or a dot in the Pocket cell — not this
      // column.
      priority: 'secondary',
      render: (run) => (
        <div className="flex flex-wrap items-center gap-2">
          {getStatusBadge(run.status, run)}
          {run.verification_mode === 1 ? (
            <Badge tone={verificationTone(run)}>{verificationLabel(run)}</Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: 'created',
      label: 'Created',
      priority: 'secondary',
      render: (run) => <span className="text-content-muted">{formatDate(run.created_at)}</span>,
    },
    {
      key: 'approved',
      label: 'Approved',
      priority: 'secondary',
      render: (run) => (
        <span className="text-content-muted">{run.approved_at ? formatDate(run.approved_at) : '—'}</span>
      ),
    },
  ];

  // Only 'pending_approval' runs are re-runnable — matches the backend guard in
  // POST /api/bill-runs/:id/rerun. Selection carries the ENTITY because the
  // server-side status filter replaces `billRuns` with just the filtered slice.
  const selection: SelectionConfig<BillRun> = {
    isSelectable: (run) => run.status === 'pending_approval',
    isSelected: (run) => selectedRuns.has(run.id),
    onToggleRow: onToggleRun,
    onToggleAll,
    allSelected: allEligibleSelected,
    someSelected: someEligibleSelected,
    label: 'Select all re-runnable runs on this page',
    // Includes the period range in every branch (not just the no-pocket-name
    // fallback) so two pending_approval runs for the same pocket in
    // different periods still get distinct accessible names.
    rowLabel: (run) =>
      `Select ${run.pocket_name || 'run'} for re-run (${formatDate(run.period_start)} - ${formatDate(run.period_end)})`,
  };

  return (
    <DataTable
      data={runs}
      columns={columns}
      rowKey={(run) => run.id}
      /* Density is per-breakpoint, and this is the table spec §3 D3's
         "admins scan 40-row bill runs" was written about — so `comfortable`
         stays exactly as it is at `lg`+, where the user has confirmed the
         layout, and `compact` applies only below it.
         `density` is a single prop with no breakpoint variants inside
         DataTable (CELL_PAD is a plain lookup), so the breakpoint has to be
         chosen here; `useIsDesktop` is the same hook and the same `lg` query
         DataTable uses for its own collapse decision.
         What it buys: `comfortable` is px-5, `compact` px-3 — 16px per cell,
         and both surviving columns below `lg` pay it, so 32px of a 309px
         budget (see BillRuns.tsx's page-padding note) went to padding that a
         phone cannot afford. `compact` is also the honest reading of the
         spec: density is a token precisely so that dense admin tables can opt
         in, and 40 bill runs on a phone is that case. No touch target
         shrinks: the only interactive things in a narrow row are DataTable's
         own checkbox label and expand toggle, both of which carry the 44px
         floor on themselves, so they — not the cell padding — set the row
         height whichever density is in force. (Phrased without naming the
         raw utilities: src/test/tapTarget.test.ts is a text scan whose
         comment detection only skips `//` and `*`-prefixed lines, so the
         literal token inside a JSX block comment like this one reads as a
         bare usage and fails the guard.) */
      density={isDesktop ? 'comfortable' : 'compact'}
      selection={eligibleCount > 0 ? selection : undefined}
      /* `lg:min-h-0`: this is THE table the density regression was raised
         against — comfortable (px-5 py-4) admin bill-run scanning, which is
         still exactly what `lg`+ gets and is the only width at which these
         actions are a row cell at all, so the escape lands where it always
         did. Spec §3 D3's "admins scan 40-row bill runs". An
         unconditional 44px row action turns a ~52px row into ~76px. 44px is
         a TOUCH floor, not a desktop target; below `lg` it still applies.
         Full rationale on Tenants.tsx's rowActions; escape documented at
         tailwind.config.js:103. */
      rowActions={(run) => (
        <Link to={`/bill-runs/${run.id}`} className="tap-target lg:min-h-0 text-accent font-medium">
          View
        </Link>
      )}
      emptyState={
        <>
          <svg className="w-12 h-12 mx-auto mb-4 text-content-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p className="text-lg font-medium">No bill runs found</p>
          <p className="mt-1">Create a new bill run to get started</p>
        </>
      }
    />
  );
}
